// You'll find that downshift is a primitive component and
// you'll be most successful wrapping it with another component
// like the MultiDownshift one you see here:
// Referencing this example:
// https://codesandbox.io/s/github/kentcdodds/downshift-examples/tree/master/?module=%2Fsrc%2Fordered-examples%2F04-multi-select.js&moduleview=1
import {
  getFilteredCities,
  updateFilteredCities,
} from '../../actions/UserActions.js'
import {
  getLocalizedCountryName,
  getLocalizedProvinceName,
  getLocalizedProvinceLabel,
} from '../../helpers/lang'
import useLanguage from '../../state/hooks/useLanguage'
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select as MuiSelect,
} from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import Downshift from 'downshift'
import matchSorter from 'match-sorter'
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { FormattedMessage, injectIntl } from 'react-intl'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

function MultiDownshift({
  selectedCities,
  onSelect,
  onChange,
  children,
  render,
  ...props
}) {
  const [selectedItems, setSelectedItems] = useState(selectedCities || [])
  const prevSelectedCitiesRef = useRef(selectedCities)

  useEffect(() => {
    if (selectedCities !== prevSelectedCitiesRef.current) {
      prevSelectedCitiesRef.current = selectedCities
      setSelectedItems(selectedCities || [])
    }
  }, [selectedCities])

  const stateReducer = useCallback((state, changes) => {
    switch (changes.type) {
      case Downshift.stateChangeTypes.keyDownEnter:
      case Downshift.stateChangeTypes.clickItem:
        return {
          ...changes,
          highlightedIndex: state.highlightedIndex,
          isOpen: true,
          inputValue: '',
        }
      default:
        return changes
    }
  }, [])

  const getStateAndHelpers = useCallback(
    (downshift) => {
      return {
        getRemoveButtonProps,
        removeItem,
        selectedItems,
        ...downshift,
      }
    },
    [selectedItems]
  )

  const removeItem = useCallback(
    (item, cb, downshift) => {
      setSelectedItems((prevItems) => {
        const newItems = prevItems.filter((i) => i !== item)
        if (onChange) {
          onChange(newItems, getStateAndHelpers(downshift))
        }
        return newItems
      })
      if (cb) cb()
    },
    [onChange, getStateAndHelpers]
  )

  const addSelectedItem = useCallback((item, cb) => {
    setSelectedItems((prevItems) => [...prevItems, item])
    if (cb) cb()
  }, [])

  const handleSelection = useCallback(
    (selectedItem, downshift) => {
      const callOnChange = () => {
        if (onSelect) {
          onSelect(selectedItems, getStateAndHelpers(downshift))
        }
        if (onChange) {
          onChange(selectedItems, getStateAndHelpers(downshift))
        }
      }
      if (selectedItems.includes(selectedItem)) {
        removeItem(selectedItem, callOnChange, downshift)
      } else {
        addSelectedItem(selectedItem, callOnChange)
      }
    },
    [
      selectedItems,
      onSelect,
      onChange,
      removeItem,
      addSelectedItem,
      getStateAndHelpers,
    ]
  )

  const getRemoveButtonProps = useCallback(
    ({ onClick, item, ...buttonProps } = {}) => {
      return {
        onClick: (e) => {
          if (onClick) onClick(e)
          e.stopPropagation()
          removeItem(item)
        },
        ...buttonProps,
      }
    },
    [removeItem]
  )

  const childrenOrRender = children || render

  return (
    <Downshift
      {...props}
      stateReducer={stateReducer}
      onChange={handleSelection}
      selectedItem={null}
    >
      {(downshift) => childrenOrRender(getStateAndHelpers(downshift))}
    </Downshift>
  )
}

function Select({
  selectedCities,
  allCities,
  userStore,
  actions,
  intl,
  onCloseDialog,
}) {
  const [, setSelectedItems] = useState(selectedCities || [])
  const [filterCountry, setFilterCountry] = useState('')
  const [filterState, setFilterState] = useState('')
  const inputRef = useRef(null)
  const prevSelectedCitiesRef = useRef(selectedCities)
  const { language } = useLanguage()

  const countryOptions = useMemo(() => {
    if (!allCities || !allCities.length) return []
    const countries = [
      ...new Set(
        allCities
          .map((c) => getLocalizedCountryName(c, language))
          .filter(Boolean)
      ),
    ]
    return countries.sort()
  }, [allCities, language])

  const stateOptions = useMemo(() => {
    if (!filterCountry || !allCities || !allCities.length) return []
    const states = [
      ...new Set(
        allCities
          .filter(
            (c) =>
              getLocalizedCountryName(c, language) === filterCountry &&
              getLocalizedProvinceName(c, language)
          )
          .map((c) => getLocalizedProvinceName(c, language))
      ),
    ]
    return states.sort()
  }, [allCities, filterCountry, language])

  const stateLabel = useMemo(() => {
    if (!filterCountry || !allCities || !allCities.length) return ''
    const cityInCountry = allCities.find(
      (c) =>
        getLocalizedCountryName(c, language) === filterCountry &&
        getLocalizedProvinceLabel(c, language)
    )
    return cityInCountry
      ? getLocalizedProvinceLabel(cityInCountry, language)
      : ''
  }, [allCities, filterCountry, language])

  useEffect(() => {
    if (selectedCities !== prevSelectedCitiesRef.current) {
      prevSelectedCitiesRef.current = selectedCities
      setSelectedItems(selectedCities || [])
    }
  }, [selectedCities])

  const itemToString = useCallback((item) => (item ? item.name : ''), [])

  const handleChange = useCallback(() => {
    // Note: do not set state or call props function in this context
  }, [])

  const compare = useCallback((a, b) => {
    if (a.name > b.name) return 1
    if (a.name < b.name) return -1
    return 0
  }, [])

  const sort = useCallback(
    (list) => {
      return list.sort((a, b) => compare(a, b))
    },
    [compare]
  )

  const getItems = useCallback(
    (filter) => {
      const results = []
      userStore
        .get('subscriptions')
        .toJS()
        .forEach((item) => {
          const validCity = allCities.find((city) => city.id === item.city_id)
          if (validCity) {
            results.push(validCity)
          }
        })
      const filtered = results.filter((city) => {
        if (
          filterCountry &&
          getLocalizedCountryName(city, language) !== filterCountry
        )
          return false
        if (
          filterState &&
          getLocalizedProvinceName(city, language) !== filterState
        )
          return false
        return true
      })
      const sortedResults = sort(
        filter ? matchSorter(filtered, filter, { keys: ['name'] }) : filtered
      )
      return [...new Set(sortedResults)]
    },
    [allCities, userStore, sort, filterCountry, filterState, language]
  )

  const renderItemTag = useCallback(
    (item, getRemoveButtonProps) => {
      const provinceName = getLocalizedProvinceName(item, language)
      return (
        <div
          item="true"
          container="true"
          key={item.id}
          style={{
            minWidth: '100px',
            background: '#31353D',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            boxSizing: 'border-box',
            borderRadius: '6px',
            padding: '5px 10px',
            marginRight: '8px',
            marginBottom: '8px',
            display: 'inline-block',
            wordWrap: 'none',
          }}
        >
          <Grid
            container
            style={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid item>
              <Typography variant="body1" style={{ fontSize: '14px' }}>
                {item.name}
              </Typography>
              <Typography
                variant="body1"
                style={{
                  fontSize: '12px',
                  opacity: 0.6,
                  maxWidth: '155px',
                }}
              >
                {provinceName
                  ? `${provinceName}, ${item.country || ''}`
                  : item.country || ''}
              </Typography>
            </Grid>
            <Grid item>
              <button
                {...getRemoveButtonProps({ item })}
                style={{
                  cursor: 'pointer',
                  lineHeight: 0.8,
                  border: 'none',
                  backgroundColor: 'transparent',
                  padding: '0',
                  fontSize: '16px',
                }}
              >
                <FontAwesomeIcon
                  icon={faTimesCircle}
                  style={{
                    fontSize: '15px',
                    marginLeft: '10px',
                    color: 'white',
                    opacity: 0.5,
                  }}
                />
              </button>
            </Grid>
          </Grid>
        </div>
      )
    },
    [language]
  )

  const renderItemInDropdown = useCallback(
    (item, index, selectedItemsList, highlightedIndex, getItemProps) => {
      if (!item) return null
      if (selectedItemsList.map((s) => s.id).includes(item.id)) return null

      const provinceName = getLocalizedProvinceName(item, language)
      return (
        <li
          style={{
            borderTop: '2px solid rgb(49, 53, 61)',
            listStyleType: 'none',
            margin: 0,
            padding: '10px 20px',
            backgroundColor:
              highlightedIndex === index
                ? 'rgb(49, 53, 61, .3)'
                : 'rgba(49, 53, 61, 0)',
          }}
          key={item.id}
          {...getItemProps({
            item,
            index,
            isActive: highlightedIndex === index,
            isSelected: selectedItemsList.includes(item),
          })}
        >
          <Typography variant="body1">{item.name}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '12px', opacity: 0.6 }}
          >
            {provinceName
              ? `${provinceName}, ${item.country || ''}`
              : item.country || ''}
          </Typography>
        </li>
      )
    },
    [language]
  )

  const renderMultiDownshiftContent = useCallback(
    ({
      getInputProps,
      getToggleButtonProps,
      getMenuProps,
      getRemoveButtonProps,
      removeItem,
      isOpen,
      inputValue,
      selectedItems: downshiftSelectedItems,
      getItemProps,
      highlightedIndex,
      toggleMenu,
    }) => {
      const dropdownProps = getMenuProps({ isOpen })

      if (!isOpen) {
        toggleMenu()
      }

      return (
        <div style={{ width: 500, margin: 'auto', position: 'relative' }}>
          <Grid
            container
            style={{
              alignItems: 'baseline',
              justifyContent: 'space-between',
              marginBottom: '10px',
            }}
          >
            <Grid item>
              <Typography variant="h5">
                <FormattedMessage id="cities.filter.addCitiesToCompare" />
              </Typography>
            </Grid>
            {Boolean(downshiftSelectedItems.length) && (
              <Grid item>
                <Typography variant="body1">
                  {downshiftSelectedItems.length === 1
                    ? intl.formatMessage({
                        id: 'cities.filter.oneCitySelected',
                      })
                    : intl.formatMessage(
                        { id: 'cities.filter.multipleCitiesSelected' },
                        { count: downshiftSelectedItems.length }
                      )}
                </Typography>
              </Grid>
            )}
          </Grid>
          <div style={{ display: 'flex', gap: '0.75rem', margin: '10px 0' }}>
            <FormControl style={{ flex: 1, minWidth: 0 }} size="small">
              <InputLabel
                shrink
                style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)' }}
              >
                {intl.formatMessage({ id: 'cityMenu.country' })}
              </InputLabel>
              <MuiSelect
                native
                value={filterCountry}
                onChange={(e) => {
                  setFilterCountry(e.target.value)
                  setFilterState('')
                }}
                style={{ fontSize: '11px', marginTop: '16px', color: 'white' }}
                inputProps={{ style: { color: 'white' } }}
              >
                <option value="" style={{ color: 'black' }}>
                  {intl.formatMessage({ id: 'cityMenu.all' })}
                </option>
                {countryOptions.map((c) => (
                  <option key={c} value={c} style={{ color: 'black' }}>
                    {c}
                  </option>
                ))}
              </MuiSelect>
            </FormControl>
            <FormControl style={{ flex: 1, minWidth: 0 }} size="small">
              <InputLabel
                shrink
                style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)' }}
              >
                {stateLabel || intl.formatMessage({ id: 'cityMenu.state' })}
              </InputLabel>
              <MuiSelect
                native
                value={filterState}
                onChange={(e) => setFilterState(e.target.value)}
                style={{ fontSize: '11px', marginTop: '16px', color: 'white' }}
                inputProps={{ style: { color: 'white' } }}
                disabled={!filterCountry}
              >
                <option value="" style={{ color: 'black' }}>
                  {intl.formatMessage({ id: 'cityMenu.all' })}
                </option>
                {stateOptions.map((s) => (
                  <option key={s} value={s} style={{ color: 'black' }}>
                    {s}
                  </option>
                ))}
              </MuiSelect>
            </FormControl>
          </div>
          <div
            style={{
              background: '#282729',
              borderRadius: '3px 3px 0 0',
              padding: '8px 12px',
            }}
            onClick={() => {
              if (
                allCities?.length !== downshiftSelectedItems.length &&
                inputRef.current
              ) {
                inputRef.current.focus()
              }
            }}
          >
            {allCities?.length === downshiftSelectedItems.length ? (
              <Typography
                variant="body1"
                style={{
                  fontStyle: 'italic',
                  opacity: '.7',
                  padding: '4px 6px',
                }}
              >
                All cities have been selected.
              </Typography>
            ) : (
              <input
                placeholder={intl.formatMessage({
                  id: 'cities.filter.placeholder',
                })}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  fontSize: '14px',
                  background: 'inherit',
                  color: 'inherit',
                  minHeight: '27px',
                  border: '0',
                  outline: 'none',
                  padding: '4px 6px',
                }}
                {...getInputProps({
                  ref: inputRef,
                  onKeyDown(event) {
                    if (event.key === 'Backspace' && !inputValue) {
                      removeItem(
                        downshiftSelectedItems[
                          downshiftSelectedItems.length - 1
                        ]
                      )
                    }
                  },
                })}
              />
            )}
          </div>
          {downshiftSelectedItems.length > 0 && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                background: '#282729',
                borderRadius: '0 0 3px 3px',
                borderTop: '1px solid rgba(255,255,255,0.1)',
                padding: '10px 12px 2px',
              }}
            >
              {downshiftSelectedItems.map((item) =>
                renderItemTag(item, getRemoveButtonProps)
              )}
            </div>
          )}
          <ul
            {...dropdownProps}
            style={{
              listStyleType: 'none',
              margin: 0,
              padding: 0,
              maxHeight: '230px',
              overflow: 'scroll',
              background: 'rgb(40, 39, 41)',
              position: 'relative',
              left: '0',
              width: '100%',
            }}
          >
            {isOpen
              ? getItems(inputValue).map((item, index) =>
                  renderItemInDropdown(
                    item,
                    index,
                    downshiftSelectedItems,
                    highlightedIndex,
                    getItemProps
                  )
                )
              : null}
          </ul>
          <Grid
            style={{
              marginTop: '10px',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              style={{ color: '#00AEEF', width: '112px' }}
              onClick={onCloseDialog}
            >
              <FormattedMessage id="cities.filter.cancel" />
            </Button>
            <Button
              style={{
                background: '#9EC73D',
                color: '#1E1D1F',
                width: '112px',
              }}
              onClick={() => {
                const userId = userStore.getIn(['userInfo', 'id'])
                actions.updateFilteredCities(downshiftSelectedItems, userId)
                onCloseDialog()
              }}
            >
              <FormattedMessage id="cities.filter.save" />
            </Button>
          </Grid>
        </div>
      )
    },
    [
      allCities,
      userStore,
      actions,
      intl,
      onCloseDialog,
      getItems,
      renderItemTag,
      renderItemInDropdown,
      filterCountry,
      filterState,
      setFilterCountry,
      setFilterState,
      countryOptions,
      stateOptions,
      stateLabel,
    ]
  )

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: '20px 18px',
        color: 'white',
      }}
    >
      <MultiDownshift
        selectedCities={selectedCities}
        onChange={handleChange}
        itemToString={itemToString}
        intl={intl}
      >
        {(props) => renderMultiDownshiftContent(props)}
      </MultiDownshift>
    </div>
  )
}

function mapStateToProps(state) {
  return {
    userStore: state.UserStore,
  }
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        updateFilteredCities,
        getFilteredCities,
      },
      dispatch
    ),
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(Select))
